<template>
  <div class="container-fluid mt-5">
    <div>
      <card
        class="no-border-card"
        body-classes="px-0 pb-1"
        footer-classes="pb-2"
      >
        <template slot="header">
          <div class="row">
            <div class="col-8">
              <h3 class="mb-0">
                <span class="text-before-button">{{ product.name }}</span>
                <base-button
                  v-if="$currentUserCan($permissions.PERM_EDIT_PRODUCTS)"
                  type="primary"
                  icon
                  size="sm"
                  @click="editProduct()"
                >
                  <span class="btn-inner--icon">
                    <i class="fas fa-edit"></i>
                  </span>
                </base-button>

                <base-button
                  v-if="$currentUserCan($permissions.PERM_DELETE_PRODUCTS)"
                  type="danger"
                  icon
                  size="sm"
                  @click="deleteProduct()"
                >
                  <span class="btn-inner--icon">
                    <i class="fas fa-trash"></i>
                  </span>
                </base-button>
              </h3>
            </div>
            <div class="col-4 text-right">
              <base-button
                @click="goBack()"
                type="button"
                class="btn btn-sm btn-primary"
              >
                {{ $t("COMMON.RETURN_TO_LIST") }}
              </base-button>

              <notification-subscription
                :objectType="'products'"
                :objectId="product.id"
                :events="{
                  UPDATE: $t('NOTIFICATIONS.EVENT_UPDATE'),
                  DELETE: $t('NOTIFICATIONS.EVENT_DELETE'),
                }"
              />
            </div>
          </div>
        </template>
        <div>
          <div
            class="col-12 justify-content-center justify-content-sm-between flex-wrap tabs-wrapper"
          >
            <tabs
              fill
              class="flex-column flex-md-row"
              tabNavWrapperClasses="nav-wrapper"
              tabNavClasses="nav nav-pills nav-fill"
              value="global"
              :key="renderKey"
            >
              <card shadow>
                <tab-pane title="global" id="1" :active="true">
                  <span slot="title">
                    <i class="ni ni-cloud-upload-96" />
                    {{ $t("COMMON.GLOBAL") }}
                  </span>
                  <product-view-global :product="product" />
                </tab-pane>

                <tab-pane title="gallery" id="2">
                  <span slot="title">
                    <i class="ni ni-cloud-upload-96" />
                    {{ $t("COMMON.GALLERY") }}
                  </span>
                  <product-view-gallery :product="product" />
                </tab-pane>

                <tab-pane
                  title="supplier-products"
                  id="3"
                  v-if="product.can_be_purchased"
                >
                  <span slot="title">
                    <i class="ni ni-circle-08" />
                    {{ $t("COMMON.SUPPLIERS") }}
                  </span>
                  <product-view-suppliers :product="product" />
                </tab-pane>

                <tab-pane
                  title="files"
                  id="998"
                  v-if="$currentUserCan($permissions.PERM_VIEW_ANY_FILES)"
                >
                  <span slot="title">
                    <i class="ni ni-folder-17" />
                    {{ $t("COMMON.FILES") }}
                  </span>
                  <list-file-component :object="product" />
                </tab-pane>

                <tab-pane
                  title="logs"
                  id="999"
                  v-if="$currentUserCan($permissions.PERM_VIEW_ANY_LOGS)"
                >
                  <span slot="title">
                    <i class="fa fa-file" />
                    {{ $t("COMMON.LOGS") }}
                  </span>
                  <product-view-logs :product="product" />
                </tab-pane>
              </card>
            </tabs>
          </div>
        </div>
      </card>
    </div>
  </div>
</template>

<script>
import {
  Table,
  TableColumn,
  DropdownMenu,
  DropdownItem,
  Dropdown,
  Tooltip,
  Select,
  Option,
  Button,
} from "element-ui";
import { cloneDeep } from "lodash";
import swal from "sweetalert2";
import requestErrorMixin from "@/mixins/request-error-mixin";
import { Tabs, TabPane } from "@/components";
import ListFileComponent from "@/components/File/ListFileComponent.vue";
import NotificationSubscription from "@/components/NotificationSubscription.vue";
import defaultProduct from "./defaultProduct";
import ProductViewGlobal from "./partials/ProductViewGlobal.vue";
import ProductViewLogs from "./partials/ProductViewLogs.vue";
import ProductViewGallery from "./partials/ProductViewGallery.vue";
import ProductViewSuppliers from "./partials/ProductViewSuppliers.vue";

export default {
  layout: "DashboardLayout",

  components: {
    [Tooltip.name]: Tooltip,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Dropdown.name]: Dropdown,
    [DropdownItem.name]: DropdownItem,
    [DropdownMenu.name]: DropdownMenu,
    [Select.name]: Select,
    [Option.name]: Option,
    [Button.name]: Button,
    Tabs,
    TabPane,
    ProductViewGlobal,
    ProductViewGallery,
    ProductViewLogs,
    ListFileComponent,
    NotificationSubscription,
    ProductViewSuppliers,
  },

  mixins: [requestErrorMixin],

  data() {
    return {
      renderKey: 0,
      product: cloneDeep(defaultProduct),
    };
  },

  computed: {},

  watch: {},

  created() {
    this.get();
  },

  methods: {
    async get() {
      try {
        const id = this.$route.params.id;
        await this.$store.dispatch("products/get", id);
        this.product = this.$store.getters["products/product"];
        this.renderKey++;
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      }
    },

    async editProduct() {
      this.$router.push({
        name: "Edit Product",
        params: { id: this.product.id },
      });
    },

    async deleteProduct() {
      const confirmation = await swal.fire({
        title: this.$t("PRODUCTS.DELETE_THIS_PRODUCT"),
        type: "question",
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: this.$t("COMMON.YES"),
        cancelButtonText: this.$t("COMMON.NO"),
        confirmButtonClass: "btn btn-primary",
        cancelButtonClass: "btn btn-warning",
      });

      try {
        if (confirmation.value === true) {
          await this.$store.dispatch("products/destroy", this.product.id);
          await this.goBack();
          this.$notify({
            type: "success",
            message: this.$t("PRODUCTS.PRODUCT_DELETED"),
          });
        }
      } catch (error) {
        await this.showRequestError(error);
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      }
    },

    goBack() {
      this.$router.push({ name: "List Products" });
    },
  },
};
</script>
